import React from "react";

import { Popover, OverlayTrigger } from "react-bootstrap";
import { LineChart, Line, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer } from "recharts";

import moment from "moment";
import numeral from "numeral";

import styles from "./style.module.css";

import { UNIFIED_PROJECT_STEPS, UNIFIED_PROJECT_STEP_ORDER } from "containers/UnifiedProject/reducer";

import { getSetupDifference } from "containers/Project/reducer";

import BlueprintIcon from "images/tileIcons/blueprint_white.svg";

const CustomToolTip = (props) => {
  const { active, payload, label } = props;
  if (!active || !payload) {
    return null;
  }

  const payloadData = payload && payload.length > 0 ? payload[0].payload : [];

  return (
    <div className={styles.cTooltip} style={{ fontSize: "14px" }}>
      {Object.keys(payloadData).map((item, i) => (
        <div className={styles.tBody} key={i}>
          <div className={styles.tLabel}>{item + ":"}</div>
          <div className={styles.tBold}>{payloadData[item]}</div>
        </div>
      ))}
    </div>
  );
};

export default class Audit extends React.Component {
  renderGraph() {
    const { project, optimizer } = this.props;

    const scenarios =
      project && project.settings && project.settings.scenarios
        ? project.settings.scenarios.map((s, i) =>
            Object.keys(s).filter((k) => k === "Overall").length > 0
              ? {
                  setup: s["Overall"].setup.data,
                  author: project.settings.originalScenarios[i].created_by,
                  createDate: project.settings.originalScenarios[i].created_date,
                  name: project.settings.originalScenarios[i].name,
                  scen: s
                }
              : undefined
          )
        : [];

    const data = scenarios
      ? scenarios
          .filter((s) => s && s.scen && optimizer && s.scen[optimizer.drilldownValue])
          .map((s) => ({
            ["Scenario Name"]: s.name,
            Profit: s.scen[optimizer.drilldownValue].profit,
            Revenue: s.scen[optimizer.drilldownValue].revenue,
            Cost: s.scen[optimizer.drilldownValue].cost,
            Author: s.author.fullname,
            Date: moment.utc(s.createDate).local().format("MM/DD/YYYY h:mm:ss a")
          }))
      : [];

    return (
      <div className={styles.auditGraph}>
        <ResponsiveContainer>
          <LineChart
            data={data}
            margin={{
              top: 5,
              right: 30,
              left: 20,
              bottom: 5
            }}
          >
            <XAxis dataKey="Date" />
            <YAxis />
            <Tooltip content={<CustomToolTip />} />
            <Legend />
            <Line type="monotone" dataKey="Profit" stroke="#8884d8" activeDot={{ r: 8 }} />
            <Line type="monotone" dataKey="Revenue" stroke="green" activeDot={{ r: 8 }} />
            <Line type="monotone" dataKey="Cost" stroke="red" activeDot={{ r: 8 }} />
          </LineChart>
        </ResponsiveContainer>
      </div>
    );
  }

  render() {
    const { optimized, project, modelGroups, blueprints, optimizer } = this.props;

    const scenarios =
      project && project.settings && project.settings.scenarios
        ? project.settings.scenarios
            .map((s, i) =>
              Object.keys(s).filter((k) => k === "Overall").length > 0
                ? {
                    setup: s["Overall"].setup.data,
                    author: project.settings.originalScenarios[i].created_by,
                    createDate: project.settings.originalScenarios[i].created_date,
                    name: project.settings.originalScenarios[i].name,
                    scen: s
                  }
                : undefined
            )
            .reverse()
        : [];

    return (
      <div>
        {this.renderGraph()}
        <div className={styles.auditTable}>
          {scenarios.map((scenario, i) => (
            <div className={styles.auditUser} key={i}>
              {Object.keys(scenario.scen)
                .filter((k) => Object.keys(scenario.scen).length === 1 || k !== "Overall")
                .filter((k) => optimizer.drilldownValue === "Overall" || optimizer.drilldownValue === k)
                .map((subgroup, j) => {
                  const activeSeg =
                    modelGroups &&
                    modelGroups.drilldownValues &&
                    modelGroups.drilldownValues.filter((v) => v.value === subgroup).length > 0
                      ? modelGroups.drilldownValues.filter((v) => v.value === subgroup)[0]
                      : undefined;

                  const diffApproved = getSetupDifference(
                    activeSeg && activeSeg.originalSetup
                      ? activeSeg.originalSetup
                      : project.segments[0].assumption.data,
                    scenario.scen[subgroup].setup.data,
                    activeSeg ? activeSeg.runId : project.segments[0].job.job_id
                  );

                  const diffLast =
                    i >= scenarios.length - 1
                      ? getSetupDifference(
                          {},
                          scenario.scen[subgroup].setup.data,
                          undefined,
                          scenario.scen[subgroup].runId
                        )
                      : getSetupDifference(
                          scenarios[i + 1].scen[subgroup].setup.data,
                          scenario.scen[subgroup].setup.data,
                          scenarios[i + 1].scen[subgroup].runId,
                          scenario.scen[subgroup].runId
                        );

                  const isSame = Object.keys(diffLast).reduce((a, b) => a && diffLast[b].length === 0, true);

                  const blueprintId =
                    diffLast &&
                    diffLast["dataset"] &&
                    diffLast["dataset"].filter((a) => Object.keys(a).indexOf("blueprintId") !== -1).length > 0
                      ? diffLast["dataset"].filter((a) => Object.keys(a).indexOf("blueprintId") !== -1)[0][
                          "blueprintId"
                        ]
                      : undefined;

                  const retBlueprint =
                    blueprintId && blueprints && blueprints.filter((b) => b.id === blueprintId).length > 0
                      ? blueprints.filter((b) => b.id === blueprintId)[0]
                      : undefined;

                  return isSame ? (
                    <div />
                  ) : (
                    <div className={styles.auditRow} key={j}>
                      <div
                        className={styles.auditFirst}
                        style={{
                          background: i % 2 !== 0 ? "#F2F3F7" : "white",
                          ...(project && project.segments && project.segments.length > 1 ? { minWidth: "170px" } : {})
                        }}
                      >
                        {j === 0 && (
                          <div>
                            <div>{scenario.author.fullname}</div>
                            <div>{scenario.name}</div>
                          </div>
                        )}
                        {retBlueprint ? (
                          <div
                            className={styles.filledItem}
                            style={{
                              background: "#4A90E2",
                              color: "white",
                              height: "20px"
                            }}
                          >
                            <img
                              src={BlueprintIcon}
                              style={{
                                marginRight: "5px",
                                height: "15px",
                                width: "15px"
                              }}
                            />
                            {retBlueprint.name}
                          </div>
                        ) : (
                          <div />
                        )}
                      </div>
                      {UNIFIED_PROJECT_STEP_ORDER.filter(
                        (s) => (optimized && optimized.hasLinkedProject) || s !== "BALANCE"
                      ).map((s, j) => {
                        const diffItems = diffApproved && diffApproved[s] ? diffApproved[s] : [];

                        const hasChanged = i >= scenarios.length - 1 || (diffLast[s] && diffLast[s].length > 0);
                        const isApproved = diffItems && diffItems.length === 0;

                        const diffLastItems = diffLast && diffLast[s] ? diffLast[s] : [];

                        return (
                          <div
                            className={styles.auditItem}
                            key={j}
                            style={i % 2 !== 0 ? { background: "#F2F3F7" } : { background: "white" }}
                          >
                            {s === "INTEGRATE" || s === "MONITOR" || s === UNIFIED_PROJECT_STEPS.TEST.id ? (
                              <div />
                            ) : hasChanged && isApproved ? (
                              <OverlayTrigger
                                placement="top"
                                trigger={["hover"]}
                                rootClose
                                overlay={
                                  <Popover id="popover-positioned-top">
                                    <div className={styles.tileOverlay}>
                                      <div className={styles.tileTitle}>
                                        {scenario
                                          ? (scenario.author ? scenario.author.fullname + ": " : "") + scenario.name
                                          : ""}
                                      </div>
                                      {s === UNIFIED_PROJECT_STEPS.DEPLOY.id && (
                                        <div className={styles.sResults}>
                                          <div className={styles.sResultItem}>
                                            <span className={styles.setupItem}>{"Deployed Scenario"}</span>
                                            <span className={styles.setupResult}>{"Complete"}</span>
                                          </div>
                                        </div>
                                      )}
                                      {s === UNIFIED_PROJECT_STEPS.TRAIN.id && (
                                        <div className={styles.sResults}>
                                          <div className={styles.sResultItem}>
                                            <span className={styles.setupItem}>{"Model Training"}</span>
                                            <span className={styles.setupResult}>{"Complete"}</span>
                                          </div>
                                        </div>
                                      )}
                                      {s === UNIFIED_PROJECT_STEPS.OPTIMIZE.id && (
                                        <div className={styles.sResults}>
                                          <div className={styles.sResultItem}>
                                            <span className={styles.setupItem}>{"Profit:"}</span>
                                            <span className={styles.setupResult}>
                                              {numeral(scenario.scen[subgroup].profit).format("0.0a")}
                                            </span>
                                          </div>
                                          <div className={styles.sResultItem}>
                                            <span className={styles.setupItem}>{"Revenue:"}</span>
                                            <span className={styles.setupResult}>
                                              {numeral(scenario.scen[subgroup].revenue).format("0.0a")}
                                            </span>
                                          </div>
                                          <div className={styles.sResultItem}>
                                            <span className={styles.setupItem}>{"Cost:"}</span>
                                            <span className={styles.setupResult}>
                                              {numeral(scenario.scen[subgroup].cost).format("0.0a")}
                                            </span>
                                          </div>
                                        </div>
                                      )}
                                      <div className={styles.nResults}>
                                        {diffLastItems.map((l) =>
                                          Object.keys(l).map((k) => (
                                            <div key={k}>
                                              <span className={styles.setupItem}>{k + ": "}</span>
                                              <span
                                                className={styles.setupResult}
                                                style={s === "REALITY_CHECK" ? { minWidth: "130px" } : {}}
                                              >
                                                {l[k]}
                                              </span>
                                            </div>
                                          ))
                                        )}
                                      </div>
                                    </div>
                                    <div className={styles.aDate}>
                                      {moment.utc(scenario.createDate).local().fromNow()}
                                    </div>
                                  </Popover>
                                }
                              >
                                <div
                                  className={styles.filledItem}
                                  title={moment.utc(scenario.createDate).local().format("MMMM Do YYYY, h:mm:ss a")}
                                  style={activeSeg && activeSeg.color ? { background: activeSeg.color } : {}}
                                >
                                  {moment.utc(scenario.createDate).local().fromNow()}
                                </div>
                              </OverlayTrigger>
                            ) : hasChanged && diffItems.length > 0 ? (
                              <OverlayTrigger
                                placement="top"
                                trigger={["hover"]}
                                rootClose
                                overlay={
                                  <Popover id="popover-positioned-top">
                                    <div className={styles.tileOverlay}>
                                      <div className={styles.tileTitle}>
                                        {scenario
                                          ? (scenario.author ? scenario.author.fullname + ": " : "") + scenario.name
                                          : ""}
                                      </div>
                                      {s === UNIFIED_PROJECT_STEPS.OPTIMIZE.id && (
                                        <div className={styles.sResults}>
                                          <div className={styles.sResultItem}>
                                            <span className={styles.setupItem}>{"Profit:"}</span>
                                            <span className={styles.setupResult}>
                                              {numeral(scenario.scen[subgroup].profit).format("0.0a")}
                                            </span>
                                          </div>
                                          <div className={styles.sResultItem}>
                                            <span className={styles.setupItem}>{"Revenue:"}</span>
                                            <span className={styles.setupResult}>
                                              {numeral(scenario.scen[subgroup].revenue).format("0.0a")}
                                            </span>
                                          </div>
                                          <div className={styles.sResultItem}>
                                            <span className={styles.setupItem}>{"Cost:"}</span>
                                            <span className={styles.setupResult}>
                                              {numeral(scenario.scen[subgroup].cost).format("0.0a")}
                                            </span>
                                          </div>
                                        </div>
                                      )}
                                      <div className={styles.nResults}>
                                        {diffItems.map((l) =>
                                          Object.keys(l).map((k) => (
                                            <div key={k}>
                                              <span className={styles.setupItem}>{k + ": "}</span>
                                              <span className={styles.setupResult}>{l[k]}</span>
                                            </div>
                                          ))
                                        )}
                                      </div>
                                    </div>
                                    <div className={styles.aDate}>
                                      {moment.utc(scenario.createDate).local().fromNow()}
                                    </div>
                                  </Popover>
                                }
                              >
                                <div
                                  className={styles.filledItem}
                                  style={{
                                    height: "10px",
                                    background: activeSeg && activeSeg.color ? activeSeg.color : "rgb(204, 204, 204)"
                                  }}
                                ></div>
                              </OverlayTrigger>
                            ) : (
                              <div />
                            )}
                          </div>
                        );
                      })}
                    </div>
                  );
                })}
            </div>
          ))}
        </div>
      </div>
    );
  }
}
